import { useEffect, useState } from "react";
import { message } from "antd";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  clientsService,
  type ContractDraft,
  type NewClientDraft,
  type NewClientResult,
} from "@/services/clientsService";
import { useWorkspaceStore } from "@/stores/useWorkspaceStore";

import { NewClientFields, type NewClientFieldsValue } from "./NewClientFields";
import { StaffMemberPicker } from "./StaffMemberPicker";

const EMPTY_NEW_CLIENT: NewClientFieldsValue = {
  website: "",
  services: null,
  approval_required: null,
  confirm_ads_armed: false,
};

/** The toast after a successful New client (design §6). */
function successSummary(result: NewClientResult): string {
  const workspace = result.workspace.created ? "workspace created" : "workspace reused";
  const n = result.members_added.length;
  const members = `${n} member${n === 1 ? "" : "s"} added`;
  return `${result.workspace.name}: contract created · ${workspace} · ${members}`;
}

type ApiError = { response?: { status?: number; data?: { detail?: unknown } } };

/** What to show for a failed create: 409 inline, 422 the first message
 *  (inline), anything else as one error toast (design §6). */
function createErrorMessage(err: unknown): { text: string; inline: boolean } {
  const response = (err as ApiError)?.response;
  const detail = response?.data?.detail;
  if (response?.status === 409 && detail && typeof detail === "object" && "message" in detail) {
    return { text: String((detail as { message: unknown }).message), inline: true };
  }
  if (response?.status === 422 && Array.isArray(detail) && detail.length > 0) {
    const first = detail[0] as { msg?: unknown };
    return { text: String(first?.msg ?? "Check the form and try again."), inline: true };
  }
  if (typeof detail === "string") return { text: detail, inline: false };
  if (detail && typeof detail === "object" && "message" in detail) {
    return { text: String((detail as { message: unknown }).message), inline: false };
  }
  return { text: "Save failed", inline: false };
}

/**
 * Create/edit dialog for a client. Create mode (contractId == null) is the
 * one-step "New client" (feature C, design §6): it adds the website preview,
 * services, approval and team members and calls createClient. Edit mode is
 * unchanged and never shows the create-only fields (R12: main reuses this
 * dialog for Details edit). A 503 means authoring is flag-dark on this env.
 */
export function ClientFormDialog({
  open,
  onOpenChange,
  contractId,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null = create a new client; an id = edit that contract. */
  contractId: string | null;
  onSaved: () => void;
}) {
  const editing = contractId != null;
  const [draft, setDraft] = useState<ContractDraft>({ name: "" });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Create mode only (R12): the New client fields and the picked members.
  const [fields, setFields] = useState<NewClientFieldsValue>(EMPTY_NEW_CLIENT);
  const [members, setMembers] = useState<string[]>([]);
  const [fieldsValid, setFieldsValid] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setInlineError(null);
    if (!editing) {
      // Every open starts empty: no website, tick or picks carried over (R19).
      setDraft({ name: "", billing_cycle_start_day: 1 });
      setFields(EMPTY_NEW_CLIENT);
      setMembers([]);
      setFieldsValid(false);
      return;
    }
    setLoading(true);
    clientsService
      .detail(contractId as string)
      .then((d) =>
        setDraft({
          name: d.name ?? "",
          website: d.website,
          monthly_points: d.monthly_points,
          monthly_budget_dollars: d.monthly_budget_dollars,
          billing_cycle_start_day: d.billing_cycle_start_day ?? 1,
          main_poc: d.main_poc,
          contact_email: d.contact_email,
        }),
      )
      .catch(() => message.error("Couldn't load the contract"))
      .finally(() => setLoading(false));
  }, [open, editing, contractId]);

  const set = (patch: Partial<ContractDraft>) => setDraft((d) => ({ ...d, ...patch }));
  const numeric = (v: string) => (v.trim() === "" ? null : Number(v));

  const createReady =
    fieldsValid && fields.services !== null && fields.approval_required !== null;

  const saveEdit = async () => {
    try {
      await clientsService.update(contractId as string, draft);
      message.success("Client updated — Planner first, mirror follows");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      message.error(detail ?? "Save failed");
    }
  };

  const saveCreate = async () => {
    if (!createReady || fields.services === null || fields.approval_required === null) return;
    const body: NewClientDraft = {
      ...draft,
      website: fields.website,
      services: fields.services,
      approval_required: fields.approval_required,
      confirm_ads_armed: fields.confirm_ads_armed,
      member_user_ids: members,
    };
    let result: NewClientResult;
    try {
      result = await clientsService.createClient(body);
    } catch (err) {
      const { text, inline } = createErrorMessage(err);
      if (inline) setInlineError(text);
      else message.error(text);
      return;
    }
    message.success(successSummary(result));
    for (const warning of result.warnings) message.warning(warning.message);
    onOpenChange(false);
    onSaved();
    // Show the new workspace in the switcher; a failed refresh never undoes
    // the create (the server also clears the access cache).
    void Promise.resolve()
      .then(() => useWorkspaceStore.getState().refreshWorkspaces())
      .catch(() => undefined);
  };

  const save = async () => {
    if (saving) return;
    if (!draft.name.trim()) {
      message.warning("Client name is required");
      return;
    }
    setSaving(true);
    setInlineError(null);
    try {
      await (editing ? saveEdit() : saveCreate());
    } finally {
      setSaving(false);
    }
  };

  const field = (
    label: string,
    node: React.ReactNode,
  ) => (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {node}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit client" : "New client"}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-6 text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              {field(
                "Name *",
                <Input value={draft.name} onChange={(e) => set({ name: e.target.value })} />,
              )}
            </div>
            {editing ? (
              <div className="col-span-2">
                {field(
                  "Website (maps the tenant)",
                  <Input
                    value={draft.website ?? ""}
                    placeholder="acme.com"
                    onChange={(e) => set({ website: e.target.value || null })}
                  />,
                )}
              </div>
            ) : (
              <div className="col-span-2">
                <NewClientFields
                  value={fields}
                  onChange={setFields}
                  onValidityChange={setFieldsValid}
                  membersSlot={<StaffMemberPicker value={members} onChange={setMembers} />}
                />
              </div>
            )}
            {field(
              "Points / month",
              <Input
                type="number"
                min={0}
                value={draft.monthly_points ?? ""}
                onChange={(e) => set({ monthly_points: numeric(e.target.value) })}
              />,
            )}
            {field(
              "Ad budget ($ / month)",
              <Input
                type="number"
                min={0}
                value={draft.monthly_budget_dollars ?? ""}
                onChange={(e) => set({ monthly_budget_dollars: numeric(e.target.value) })}
              />,
            )}
            {field(
              "Billing cycle day",
              <Select
                value={String(draft.billing_cycle_start_day ?? 1)}
                onValueChange={(v) => set({ billing_cycle_start_day: Number(v) })}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1st</SelectItem>
                  <SelectItem value="15">15th</SelectItem>
                </SelectContent>
              </Select>,
            )}
            {field(
              "Main POC",
              <Input
                value={draft.main_poc ?? ""}
                onChange={(e) => set({ main_poc: e.target.value || null })}
              />,
            )}
            <div className="col-span-2">
              {field(
                "Contact email",
                <Input
                  type="email"
                  value={draft.contact_email ?? ""}
                  onChange={(e) => set({ contact_email: e.target.value || null })}
                />,
              )}
            </div>
          </div>
        )}
        {inlineError && (
          <p role="alert" className="text-sm text-destructive">
            {inlineError}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving || loading || !draft.name.trim() || (!editing && !createReady)}
          >
            {saving ? "Saving…" : editing ? "Save changes" : "Create client"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
